/**
 * 卷 HTTP API 服务
 *
 * 封装卷相关的 REST API 调用，用于卷 CRUD 操作。
 * 使用 HttpOnly Cookie 进行认证，浏览器自动携带 Token。
 */

import type { Volume, VolumeCreate, VolumeUpdate } from '../types/volume';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class UnauthorizedError extends HttpError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class NotFoundError extends HttpError {
  constructor(message: string = '资源不存在') {
    super(message, 404, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class ValidationError extends HttpError {
  constructor(message: string) {
    super(message, 400, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

export class OverlappingRangeError extends HttpError {
  constructor(message: string) {
    super(message, 400, 'OVERLAPPING_RANGE');
    this.name = 'OverlappingRangeError';
  }
}

export class DuplicateNameError extends HttpError {
  constructor(message: string) {
    super(message, 400, 'DUPLICATE_NAME');
    this.name = 'DuplicateNameError';
  }
}

interface VolumeListResponse {
  volumes: Volume[];
  total: number;
}

function getHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
  }

  switch (response.status) {
    case 400:
      if (errorMessage.includes('重叠') || errorMessage.includes('overlap')) {
        throw new OverlappingRangeError(errorMessage);
      }
      if (errorMessage.includes('已存在') || errorMessage.includes('重复')) {
        throw new DuplicateNameError(errorMessage);
      }
      throw new ValidationError(errorMessage);
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 404:
      throw new NotFoundError(errorMessage);
    default:
      throw new HttpError(errorMessage, response.status);
  }
}

export class VolumeApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * 获取工作区的所有卷
   */
  async listVolumes(workspaceId: number): Promise<Volume[]> {
    const response = await fetch(
      `${this.baseUrl}/api/volumes/workspaces/${workspaceId}`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data: VolumeListResponse = await response.json();
    return data.volumes;
  }

  /**
   * 创建新卷
   */
  async createVolume(workspaceId: number, data: VolumeCreate): Promise<Volume> {
    const response = await fetch(
      `${this.baseUrl}/api/volumes/workspaces/${workspaceId}`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 更新卷信息
   */
  async updateVolume(volumeId: number, data: VolumeUpdate): Promise<Volume> {
    const response = await fetch(
      `${this.baseUrl}/api/volumes/${volumeId}`,
      {
        method: 'PUT',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 删除卷
   */
  async deleteVolume(volumeId: number): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/api/volumes/${volumeId}`,
      {
        method: 'DELETE',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }
  }
}

export const volumeApi = new VolumeApiService();

export default volumeApi;
